import React, { useState, useEffect } from 'react';
import { createCompanyProfile, uploadCompanyLogo, uploadCompanyImages, updateCompanyProfile } from '../services/companyService';
import { locationsByState } from '../constants/locations';

// Bilingual Label Component
const BilingualLabel = ({ english, marathi, required = false, className = "" }) => (
  <label className={`block text-sm font-medium text-gray-700 ${className}`}>
    {english} <span className="text-gray-500">/ {marathi}</span>
    {required && <span className="text-red-500 ml-1">*</span>}
  </label>
);

const CompanyProfileForm = ({ user, onComplete, initialData = null }) => {
  const logoInputRef = React.useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    // Basic Information
    companyName: '',
    industry: '',
    companySize: '',
    companyType: '',
    foundedYear: '',
    location: '',
    website: '',
    description: '',

    // Company Details
    mission: '',
    vision: '',
    values: '',
    culture: '',
    benefits: '',

    // Contact Information
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    district: '',
    country: 'India',
    zipCode: '',

    // Social Media
    linkedinUrl: '',
    twitterUrl: '',
    facebookUrl: '',
    instagramUrl: '',

    // Images
    logoUrl: '',
    companyImages: [],

    // Recruiters
    recruiters: []
  });

  const [newRecruiter, setNewRecruiter] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    bio: ''
  });

  const [logoFile, setLogoFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [logoPreview, setLogoPreview] = useState('');

  // Populate form with initial data if provided
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));

      if (initialData.logoUrl) {
        setLogoPreview(initialData.logoUrl);
      }
    }
  }, [initialData]);

  const steps = [
    { id: 1, title: 'Basic Information', description: 'Company details and overview' },
    // { id: 2, title: 'Company Culture', description: 'Mission, vision, and values' }, // Hidden temporarily
    { id: 2, title: 'Contact & Location', description: 'Contact information and address' },
    { id: 3, title: 'Media & Branding', description: 'Logo and company images' },
    { id: 4, title: 'Recruiter Team', description: 'Add your recruitment team' },
    { id: 5, title: 'Review & Submit', description: 'Review and complete profile' }
  ];

  const industries = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'Manufacturing',
    'Retail', 'Consulting', 'Media & Entertainment', 'Real Estate',
    'Transportation', 'Energy', 'Government', 'Non-Profit', 'Other'
  ];

  const companySizes = [
    '1-10 employees', '11-50 employees', '51-200 employees',
    '201-500 employees', '501-1000 employees', '1001-5000 employees',
    '5000+ employees'
  ];

  const companyTypes = [
    'Public Company', 'Private Company', 'Non-Profit', 'Government',
    'Startup', 'Enterprise', 'SME', 'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Reset District if State changes
    if (name === 'state') {
      setFormData(prev => ({
        ...prev,
        state: value,
        district: '',
        location: value ? `${prev.city ? prev.city + ', ' : ''}${value}, India` : prev.location
      }));
    }

    if (name === 'district') {
      setFormData(prev => ({
        ...prev,
        district: value
      }));
    }

    if (name === 'city') {
      setFormData(prev => ({
        ...prev,
        city: value,
        location: value ? `${value}, ${prev.state ? prev.state + ', ' : ''}India` : prev.location
      }));
    }
  };

  const handleRecruiterChange = (e) => {
    const { name, value } = e.target;
    setNewRecruiter(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(prev => [...prev, ...files]);
  };

  const addRecruiterToList = () => {
    if (newRecruiter.name && newRecruiter.email) {
      setFormData(prev => ({
        ...prev,
        recruiters: [...prev.recruiters, { ...newRecruiter, id: Date.now() }]
      }));
      setNewRecruiter({
        name: '',
        email: '',
        phone: '',
        position: '',
        department: '',
        bio: ''
      });
    }
  };

  const removeRecruiter = (id) => {
    setFormData(prev => ({
      ...prev,
      recruiters: prev.recruiters.filter(recruiter => recruiter.id !== id)
    }));
  };

  const removeImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const calculateProgress = () => {
    // Only count visible required fields for progress calculation
    const requiredFields = [
      'companyName',
      'industry',
      'companySize',
      'companyType',
      'location',
      'description',
      'email'
    ];

    const totalRequired = requiredFields.length;
    const filledRequired = requiredFields.filter(field => {
      const value = formData[field];
      return value && value !== '' && value !== null && value !== undefined;
    }).length;

    return Math.round((filledRequired / totalRequired) * 100);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Final validation before submission
      if (!formData.companyName?.trim()) {
        setError('Company Name is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!formData.industry) {
        setError('Industry is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!formData.companySize) {
        setError('Company Size is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!formData.companyType) {
        setError('Company Type is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!formData.location?.trim()) {
        setError('Location is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!formData.description?.trim()) {
        setError('Company Description is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!formData.email?.trim()) {
        setError('Contact Email is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Please enter a valid email address');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      // Use user ID as company ID to ensure one company per user
      const companyId = user.uid;

      // Upload logo if selected
      if (logoFile) {
        console.log('Uploading logo...');
        const logoResult = await uploadCompanyLogo(logoFile, companyId);
        if (logoResult.success) {
          formData.logoUrl = logoResult.url;
          console.log('Logo uploaded successfully');
        } else {
          console.warn('Logo upload failed:', logoResult.error);
          // Continue without logo
        }
      }

      // Upload company images if selected
      if (imageFiles.length > 0) {
        console.log('Uploading company images...');
        const imagesResult = await uploadCompanyImages(imageFiles, companyId);
        if (imagesResult.success) {
          formData.companyImages = imagesResult.urls;
          console.log('Company images uploaded successfully');
        } else {
          console.warn('Company images upload failed:', imagesResult.error);
          // Continue without images
        }
      }

      // Create company profile
      console.log('Creating company profile...');
      const result = await createCompanyProfile(formData, user.uid);

      if (result.success) {
        setSuccess('Company profile created successfully!');
        if (onComplete) {
          onComplete(result.companyId);
        }
      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Submit error:', error);
      setError('An error occurred while creating the profile: ' + error.message);
    }

    setLoading(false);
  };

  // Validation function for each step
  const validateStep = (step) => {
    setError('');

    switch (step) {
      case 1: // Basic Information
        if (!formData.companyName?.trim()) {
          setError('Company Name is required');
          return false;
        }
        if (!formData.industry) {
          setError('Industry is required');
          return false;
        }
        if (!formData.companySize) {
          setError('Company Size is required');
          return false;
        }
        if (!formData.companyType) {
          setError('Company Type is required');
          return false;
        }
        if (!formData.description?.trim()) {
          setError('Company Description is required');
          return false;
        }
        return true;

      case 2: // Contact & Location
        if (!formData.email?.trim()) {
          setError('Contact Email is required');
          return false;
        }
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          setError('Please enter a valid email address');
          return false;
        }
        if (!formData.address?.trim()) {
          setError('Address is required');
          return false;
        }
        if (!formData.state) {
          setError('State is required');
          return false;
        }
        if (!formData.district) {
          setError('District is required');
          return false;
        }
        if (!formData.city?.trim()) {
          setError('City is required');
          return false;
        }
        if (!formData.zipCode?.trim()) {
          setError('ZIP Code is required');
          return false;
        }
        return true;

      case 3: // Media & Branding
        return true;

      case 4: // Recruiter Team (optional)
        return true;

      case 5: // Review & Submit
        return true;

      default:
        return true;
    }
  };

  const nextStep = () => {
    // Validate current step before proceeding
    if (!validateStep(currentStep)) {
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setError(''); // Clear any errors when moving to next step
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(''); // Clear any errors when moving back
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Basic Company Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <BilingualLabel english="Company Name" marathi="कंपनीचे नाव" required className="mb-2" />
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter company name / कंपनीचे नाव प्रविष्ट करा"
                  required
                />
              </div>

              <div>
                <BilingualLabel english="Industry" marathi="उद्योग" required className="mb-2" />
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Industry / उद्योग निवडा</option>
                  {industries.map(industry => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>

              <div>
                <BilingualLabel english="Company Size" marathi="कंपनीचा आकार" required className="mb-2" />
                <select
                  name="companySize"
                  value={formData.companySize}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Company Size / कंपनीचा आकार निवडा</option>
                  {companySizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>

              <div>
                <BilingualLabel english="Company Type" marathi="कंपनीचा प्रकार" required className="mb-2" />
                <select
                  name="companyType"
                  value={formData.companyType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Company Type / कंपनीचा प्रकार निवडा</option>
                  {companyTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Hidden temporarily - Founded Year */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Founded Year
                </label>
                <input
                  type="number"
                  name="foundedYear"
                  value={formData.foundedYear}
                  onChange={handleChange}
                  min="1800"
                  max={new Date().getFullYear()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div> */}

              {/* Location field removed from Step 1, will be in Step 2 with strict State/District */}
            </div>

            {/* Hidden temporarily - Website */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://www.company.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div> */}

            <div>
              <BilingualLabel english="Company Description" marathi="कंपनी वर्णन" required className="mb-2" />
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="Tell us about your company / तुमच्या कंपनीबद्दल सांगा..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
        );

      // case 2: Company Culture - Hidden temporarily
      // case 2:
      //   return (
      //     <div className="space-y-6">
      //       <h3 className="text-lg font-semibold text-gray-900">Company Culture & Values</h3>
      //       
      //       <div>
      //         <label className="block text-sm font-medium text-gray-700 mb-2">
      //           Mission Statement
      //         </label>
      //         <textarea
      //           name="mission"
      //           value={formData.mission}
      //           onChange={handleChange}
      //           rows={3}
      //           placeholder="What is your company's mission?"
      //           className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      //         />
      //       </div>
      //
      //       <div>
      //         <label className="block text-sm font-medium text-gray-700 mb-2">
      //           Vision Statement
      //         </label>
      //         <textarea
      //           name="vision"
      //           value={formData.vision}
      //           onChange={handleChange}
      //           rows={3}
      //           placeholder="What is your company's vision for the future?"
      //           className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      //         />
      //       </div>
      //
      //       <div>
      //         <label className="block text-sm font-medium text-gray-700 mb-2">
      //           Company Values
      //         </label>
      //         <textarea
      //           name="values"
      //           value={formData.values}
      //           onChange={handleChange}
      //           rows={3}
      //           placeholder="What values guide your company?"
      //           className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      //         />
      //       </div>
      //
      //       <div>
      //         <label className="block text-sm font-medium text-gray-700 mb-2">
      //           Company Culture
      //         </label>
      //         <textarea
      //           name="culture"
      //           value={formData.culture}
      //           onChange={handleChange}
      //           rows={4}
      //           placeholder="Describe your company culture and work environment..."
      //           className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      //         />
      //       </div>
      //
      //       <div>
      //         <label className="block text-sm font-medium text-gray-700 mb-2">
      //           Employee Benefits
      //         </label>
      //         <textarea
      //           name="benefits"
      //           value={formData.benefits}
      //           onChange={handleChange}
      //           rows={4}
      //           placeholder="What benefits do you offer to employees?"
      //           className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      //         />
      //       </div>
      //     </div>
      //   );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <BilingualLabel english="Contact Email" marathi="संपर्क ईमेल" required className="mb-2" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter contact email / संपर्क ईमेल प्रविष्ट करा"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Hidden temporarily - Contact Phone */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div> */}
            </div>

            <div>
              <BilingualLabel english="Address" marathi="पत्ता" required className="mb-2" />
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={2}
                placeholder="Street address / रस्त्याचा पत्ता"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <BilingualLabel english="State" marathi="राज्य" required className="mb-2" />
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select State / राज्य निवडा</option>
                  {Object.keys(locationsByState).map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div>
                <BilingualLabel english="District" marathi="जिल्हा" required className="mb-2" />
                <select
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  disabled={!formData.state}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  required
                >
                  <option value="">Select District / जिल्हा निवडा</option>
                  {formData.state && locationsByState[formData.state]?.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>

              <div>
                <BilingualLabel english="City" marathi="शहर" required className="mb-2" />
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <BilingualLabel english="ZIP/Postal Code" marathi="पिन कोड" required className="mb-2" />
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            {/* Hidden temporarily - Social Media Links */}
            {/* <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900">Social Media Links</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  LinkedIn
                </label>
                <input
                  type="url"
                  name="linkedinUrl"
                  value={formData.linkedinUrl}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/company/yourcompany"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Twitter
                </label>
                <input
                  type="url"
                  name="twitterUrl"
                  value={formData.twitterUrl}
                  onChange={handleChange}
                  placeholder="https://twitter.com/yourcompany"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facebook
                </label>
                <input
                  type="url"
                  name="facebookUrl"
                  value={formData.facebookUrl}
                  onChange={handleChange}
                  placeholder="https://facebook.com/yourcompany"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instagram
                </label>
                <input
                  type="url"
                  name="instagramUrl"
                  value={formData.instagramUrl}
                  onChange={handleChange}
                  placeholder="https://instagram.com/yourcompany"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div> */}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Media & Branding</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Logo
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  {logoPreview ? (
                    <div className="flex flex-col items-center">
                      <img src={logoPreview} alt="Logo preview" className="h-32 w-32 object-contain" />
                      <button
                        type="button"
                        onClick={() => {
                          setLogoFile(null);
                          setLogoPreview('');
                        }}
                        className="mt-2 text-sm text-red-600 hover:text-red-500"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div>
                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className="flex text-sm text-gray-600 justify-center">
                        <button
                          type="button"
                          onClick={() => logoInputRef.current?.click()}
                          className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500 touch-manipulation"
                        >
                          Upload a logo
                        </button>
                        <input
                          ref={logoInputRef}
                          id="logo-upload"
                          name="logo-upload"
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleLogoChange}
                        />
                        <p className="pl-1 text-gray-500">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Hidden temporarily - Company Images */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Images
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label htmlFor="images-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                      <span>Upload images</span>
                      <input id="images-upload" name="images-upload" type="file" className="sr-only" accept="image/*" multiple onChange={handleImagesChange} />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB each</p>
                </div>
              </div>
              
              {imageFiles.length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {imageFiles.map((file, index) => (
                    <div key={index} className="relative">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index + 1}`}
                        className="h-24 w-24 object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div> */}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Recruiter Team</h3>

            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="text-md font-medium text-gray-900 mb-4">Add New Recruiter</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={newRecruiter.name}
                    onChange={handleRecruiterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={newRecruiter.email}
                    onChange={handleRecruiterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={newRecruiter.phone}
                    onChange={handleRecruiterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Position
                  </label>
                  <input
                    type="text"
                    name="position"
                    value={newRecruiter.position}
                    onChange={handleRecruiterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department
                  </label>
                  <input
                    type="text"
                    name="department"
                    value={newRecruiter.department}
                    onChange={handleRecruiterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bio
                </label>
                <textarea
                  name="bio"
                  value={newRecruiter.bio}
                  onChange={handleRecruiterChange}
                  rows={3}
                  placeholder="Brief description of the recruiter..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                type="button"
                onClick={addRecruiterToList}
                className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Add Recruiter
              </button>
            </div>

            {formData.recruiters.length > 0 && (
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Added Recruiters</h4>
                <div className="space-y-3">
                  {formData.recruiters.map((recruiter, index) => (
                    <div key={recruiter.id} className="bg-white p-4 border rounded-md">
                      <div className="flex justify-between items-start">
                        <div>
                          <h5 className="font-medium text-gray-900">{recruiter.name}</h5>
                          <p className="text-sm text-gray-600">{recruiter.email}</p>
                          {recruiter.position && <p className="text-sm text-gray-600">{recruiter.position}</p>}
                          {recruiter.department && <p className="text-sm text-gray-600">{recruiter.department}</p>}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeRecruiter(recruiter.id)}
                          className="text-red-600 hover:text-red-500"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Review & Submit</h3>

            <div className="bg-gray-50 p-6 rounded-md">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Profile Completion</h4>
              <div className="flex items-center space-x-4">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${calculateProgress()}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-700">{calculateProgress()}%</span>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Complete all required fields to create a complete company profile.
              </p>
            </div>

            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900">Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Company Name:</span> {formData.companyName || 'Not provided'}
                </div>
                <div>
                  <span className="font-medium">Industry:</span> {formData.industry || 'Not provided'}
                </div>
                <div>
                  <span className="font-medium">Company Size:</span> {formData.companySize || 'Not provided'}
                </div>
                <div>
                  <span className="font-medium">Location:</span> {formData.location || 'Not provided'}
                </div>
                <div>
                  <span className="font-medium">Website:</span> {formData.website || 'Not provided'}
                </div>
                <div>
                  <span className="font-medium">Recruiters:</span> {formData.recruiters.length}
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md">
                {success}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Create Company Profile</h2>
        <p className="mt-2 text-gray-600 text-sm sm:text-base">
          Complete your company profile to attract the best talent and showcase your organization.
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between w-full">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center flex-1 last:flex-initial">
              <div className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full border-2 text-sm font-semibold ${currentStep >= step.id
                ? 'bg-blue-600 border-blue-600 text-white'
                : 'border-gray-300 text-gray-500'
                }`}>
                {step.id}
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 md:mx-4 ${currentStep > step.id ? 'bg-blue-600' : 'bg-gray-300'
                  }`} />
              )}
            </div>
          ))}
        </div>

        <div className="mt-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {steps[currentStep - 1].title}
          </h3>
          <p className="text-gray-600">{steps[currentStep - 1].description}</p>
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {renderStepContent()}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mt-4 bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="mt-8 flex justify-between">
        <button
          type="button"
          onClick={prevStep}
          disabled={currentStep === 1}
          className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>

        <div className="flex space-x-4">
          {currentStep < steps.length ? (
            <button
              type="button"
              onClick={nextStep}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Profile...' : 'Create Profile'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyProfileForm;
